/* eslint-disable max-lines */
import { useCallback, useEffect, useState } from "react";
import { ZOOM_CONFIG } from "../lib/constants";
import { calculatePanBounds, constrainToBounds } from "../lib/utils";
import type { Coordinates } from "./types/ArtworkPreviewTypes";

type UseZoomAndPanProps = {
  isDesktop: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  imageLoaded: boolean;
};

// Constants for wheel zoom
const WHEEL_SCALE_FACTOR = 1.1;

// eslint-disable-next-line max-lines-per-function
export const useZoomAndPan = ({ isDesktop, containerRef, imageLoaded }: UseZoomAndPanProps) => {
  const [zoom, setZoom] = useState(ZOOM_CONFIG.default);
  const [panOffset, setPanOffset] = useState<Coordinates>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState<Coordinates>({ x: 0, y: 0 });

  // Pinch gesture tracking
  const [isPinching, setIsPinching] = useState(false);
  const [lastPinchDistance, setLastPinchDistance] = useState(0);

  const updatePanOffset = useCallback(
    (delta: Coordinates) => {
      if (zoom <= ZOOM_CONFIG.default) return;

      const bounds = calculatePanBounds(zoom, { isDesktop, containerRef, imageLoaded });

      setPanOffset(prev => {
        const newCoordinates = {
          x: prev.x + delta.x,
          y: prev.y + delta.y
        };
        return constrainToBounds(newCoordinates, bounds);
      });
    },
    [zoom, isDesktop, containerRef, imageLoaded]
  );

  const updateZoomAndPan = useCallback(
    (newZoom: number) => {
      setZoom(newZoom);

      if (newZoom === ZOOM_CONFIG.min) {
        setPanOffset({ x: 0, y: 0 });
      } else {
        const bounds = calculatePanBounds(newZoom, { isDesktop, containerRef, imageLoaded });
        setPanOffset(prev => constrainToBounds(prev, bounds));
      }
    },
    [isDesktop, containerRef, imageLoaded]
  );

  // Helper to update zoom and pan while maintaining cursor position
  const zoomAroundPoint = useCallback(
    (props: {
      newZoom: number;
      cursorPos: Coordinates;
      containerWidth: number;
      containerHeight: number;
    }) => {
      const { newZoom, cursorPos, containerWidth, containerHeight } = props;
      const oldZoom = zoom;

      // If zoom hasn't changed, skip
      if (Math.abs(newZoom - oldZoom) < 0.001) return;

      // Convert cursor position to be relative to container center (since transformOrigin is center)
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;
      const cursorFromCenter = {
        x: cursorPos.x - centerX,
        y: cursorPos.y - centerY
      };

      // Calculate the point in content space (accounting for current pan and zoom from center)
      const contentPoint = {
        x: (cursorFromCenter.x - panOffset.x) / oldZoom,
        y: (cursorFromCenter.y - panOffset.y) / oldZoom
      };

      // Calculate new pan offset to keep the same content point under the cursor
      const newPan = {
        x: cursorFromCenter.x - contentPoint.x * newZoom,
        y: cursorFromCenter.y - contentPoint.y * newZoom
      };

      // Update zoom and constrain pan
      setZoom(newZoom);
      const bounds = calculatePanBounds(newZoom, { isDesktop, containerRef, imageLoaded });
      setPanOffset(constrainToBounds(newPan, bounds));
    },
    [zoom, panOffset, isDesktop, containerRef, imageLoaded]
  );

  const zoomIn = useCallback(() => {
    const newZoom = Math.min(zoom + ZOOM_CONFIG.step, ZOOM_CONFIG.max);
    updateZoomAndPan(newZoom);
  }, [zoom, updateZoomAndPan]);

  const zoomOut = useCallback(() => {
    const newZoom = Math.max(zoom - ZOOM_CONFIG.step, ZOOM_CONFIG.min);
    updateZoomAndPan(newZoom);
  }, [zoom, updateZoomAndPan]);

  const resetZoom = useCallback(() => {
    setZoom(ZOOM_CONFIG.default);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const resetPan = useCallback(() => {
    setPanOffset({ x: 0, y: 0 });
    setIsPanning(false);
  }, []);

  const startPan = useCallback(
    (point: Coordinates) => {
      if (zoom > ZOOM_CONFIG.default) {
        setIsPanning(true);
        setLastPanPoint(point);
      }
    },
    [zoom]
  );

  const continuePan = useCallback(
    (point: Coordinates) => {
      if (isPanning && zoom > ZOOM_CONFIG.default) {
        const delta = {
          x: point.x - lastPanPoint.x,
          y: point.y - lastPanPoint.y
        };

        updatePanOffset(delta);
        setLastPanPoint(point);
      }
    },
    [isPanning, zoom, lastPanPoint, updatePanOffset]
  );

  const endPan = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Mouse handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      startPan({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    },
    [startPan]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      continuePan({ x: e.clientX, y: e.clientY });
    },
    [continuePan]
  );

  // Wheel zoom handler (handles both mouse wheel and touchpad pinch gestures)
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      // Get cursor position relative to the element the handler is attached to
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const cursorPos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };

      // Calculate new zoom based on wheel direction
      const direction = e.deltaY > 0 ? -1 : 1;
      const newZoom = Math.max(
        ZOOM_CONFIG.min,
        Math.min(ZOOM_CONFIG.max, zoom * WHEEL_SCALE_FACTOR ** direction)
      );

      zoomAroundPoint({
        newZoom,
        cursorPos,
        containerWidth: rect.width,
        containerHeight: rect.height
      });
    },
    [zoom, zoomAroundPoint]
  );

  // Touch handlers with single-finger pan and pinch zoom support
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        // Single-finger pan
        const touch = e.touches[0];
        if (touch) {
          startPan({ x: touch.clientX, y: touch.clientY });
          e.preventDefault();
        }
      } else if (e.touches.length >= 2) {
        // Start pinch zoom gesture
        e.preventDefault();
        e.stopPropagation();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const distance = Math.hypot(
            touch1.clientX - touch2.clientX,
            touch1.clientY - touch2.clientY
          );
          setLastPinchDistance(distance);
          setIsPinching(true);
          setIsPanning(false);
        }
      }
    },
    [startPan]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (isPinching && e.touches.length >= 2) {
        // Handle pinch zoom
        e.preventDefault();
        e.stopPropagation();

        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const currentDistance = Math.hypot(
            touch1.clientX - touch2.clientX,
            touch1.clientY - touch2.clientY
          );
          const currentCenter = {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
          };

          if (lastPinchDistance > 0) {
            const scale = currentDistance / lastPinchDistance;
            const newZoom = Math.max(ZOOM_CONFIG.min, Math.min(ZOOM_CONFIG.max, zoom * scale));

            if (Math.abs(newZoom - zoom) > 0.001) {
              // Calculate pinch center position relative to the element the handler is attached to
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const pinchCenterX = currentCenter.x - rect.left;
              const pinchCenterY = currentCenter.y - rect.top;

              zoomAroundPoint({
                newZoom,
                cursorPos: { x: pinchCenterX, y: pinchCenterY },
                containerWidth: rect.width,
                containerHeight: rect.height
              });
            }
          }

          setLastPinchDistance(currentDistance);
        }
      } else if (e.touches.length === 1 && !isPinching) {
        // Single-finger pan
        const touch = e.touches[0];
        if (touch) {
          continuePan({ x: touch.clientX, y: touch.clientY });
          e.preventDefault();
        }
      }
    },
    [isPinching, zoom, lastPinchDistance, continuePan, zoomAroundPoint]
  );

  const handleTouchEnd = useCallback(() => {
    setIsPinching(false);
    setLastPinchDistance(0);
    endPan();
  }, [endPan]);

  // Global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      endPan();
    };

    if (isPanning) {
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("mouseleave", handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mouseleave", handleGlobalMouseUp);
    };
  }, [isPanning, endPan]);

  const canZoomIn = zoom < ZOOM_CONFIG.max;
  const canZoomOut = zoom > ZOOM_CONFIG.min;
  const zoomPercentage = Math.round(zoom * 100);

  return {
    // Zoom
    zoom,
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    zoomPercentage,
    // Pan
    panOffset,
    isPanning,
    resetPan,
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: endPan,
      onWheel: handleWheel,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    }
  };
};
